// src/pages/Swap/hooks/useWrapUnwrap.js
/* global BigInt */
import { useState, useContext, useCallback } from 'react';
import { ethers, Contract } from 'ethers';
import { useTranslation } from 'react-i18next';
import { TransactionContext, TRANSACTION_TYPES } from '../../../contexts/TransactionContext';
import { NetworkContext } from '../../../contexts/NetworkContext';
import { formatUnits, parseUnits } from 'ethers';
import { isNativeToken } from '../utils/SwapUtils';

// ABI for WETH - Just the functions we need
const WETH_ABI = [
  // Native token -> WETH (payable function)
  "function deposit() external payable",
  // WETH -> Native token
  "function withdraw(uint wad) external",
  // Check balance
  "function balanceOf(address) external view returns (uint256)",
  // Allow reading symbol
  "function symbol() external view returns (string)",
  // Allow reading name
  "function name() external view returns (string)",
  // Allow reading decimals
  "function decimals() external view returns (uint8)"
];

/**
 * Hook for handling direct wrap/unwrap operations between native tokens and their wrapped versions
 * @param {object} provider - Ethers provider
 * @param {object} currentWallet - Current wallet information
 * @param {object} currentChain - Current blockchain network
 * @param {object} notificationRef - Reference to notification component
 * @param {boolean} autoConfirm - Whether to auto-confirm transactions
 * @param {object} inputToken - Current input token (added parameter)
 * @param {object} outputToken - Current output token (added parameter)
 * @returns {object} - Wrap/unwrap related functions and state
 */
const useWrapUnwrap = (
  provider, 
  currentWallet, 
  currentChain, 
  notificationRef, 
  autoConfirm,
  inputToken, // Added input token parameter
  outputToken // Added output token parameter
) => {
  const { t } = useTranslation();
  const { addTransaction, updateTransactionStatus } = useContext(TransactionContext);
  const { currentChain: contextChain } = useContext(NetworkContext);
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [txnHash, setTxnHash] = useState(null);
  const [error, setError] = useState(null);
  const [confirmationDetails, setConfirmationDetails] = useState(null);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isPendingConfirmation, setIsPendingConfirmation] = useState(false);
  
  /**
   * Check if the operation is a direct wrap/unwrap
   * @param {object} inputToken - Input token data
   * @param {object} outputToken - Output token data
   * @returns {object|false} - Returns operation details if it's a wrap/unwrap, false otherwise
   */
  const isWrapUnwrapOperation = useCallback((inputToken, outputToken) => {
    if (!inputToken || !outputToken || !currentChain) return false;
    
    // Get addresses in lowercase for comparison
    const inputAddress = inputToken.address?.toLowerCase();
    const outputAddress = outputToken.address?.toLowerCase();
    const wrappedNativeAddress = currentChain.wrappedNativeToken?.toLowerCase();
    
    // No wrapped native token configured for this chain
    if (!wrappedNativeAddress) return false;
    
    // Case 1: Native Token to Wrapped Token (WRAP)
    if (isNativeToken(inputAddress) && outputAddress === wrappedNativeAddress) {
      return {
        type: 'wrap',
        wrappedToken: outputToken,
        nativeToken: currentChain.nativeToken
      };
    }
    
    // Case 2: Wrapped Token to Native Token (UNWRAP)
    if (inputAddress === wrappedNativeAddress && isNativeToken(outputAddress)) {
      return {
        type: 'unwrap',
        wrappedToken: inputToken,
        nativeToken: currentChain.nativeToken
      };
    }
    
    return false;
  }, [currentChain]);

  /**
   * Get the current operation based on input and output tokens
   * @returns {object|false} - The current operation or false if not a wrap/unwrap
   */
  const getCurrentOperation = useCallback(() => {
    return isWrapUnwrapOperation(inputToken, outputToken);
  }, [isWrapUnwrapOperation, inputToken, outputToken]);

/**
 * Prepare transaction confirmation details
 * @param {string} operationType - 'wrap' or 'unwrap'
 * @param {object} nativeToken - Native token data
 * @param {object} wrappedToken - Wrapped token data
 * @param {string} amount - Amount to wrap/unwrap
 * @param {string} customGasPrice - Gas price in gwei
 * @returns {object} - { success, details } object
 */
const prepareConfirmation = useCallback(async (
    operationType, 
    nativeToken, 
    wrappedToken, 
    amount,
    customGasPrice
  ) => {
    try {
      // Reset states
      setTxnHash(null);
      setError(null);
      setIsPendingConfirmation(false);
      
      if (!currentWallet?.privateKey) {
        setError("Wallet connection issue");
        return { success: false };
      }
      
      // Get wallet address
      const wallet = new ethers.Wallet(currentWallet.privateKey, provider);
      const walletAddress = wallet.address;
  
      // Estimate gas cost (rough estimate)
      const gasEstimate = BigInt(100000); // 100k gas for wrap/unwrap
      const gasPrice = await provider.getFeeData()
        .then(data => data.gasPrice || ethers.parseUnits("3", "gwei"))
        .catch(() => ethers.parseUnits("3", "gwei"));
  
      const gasCost = gasEstimate * gasPrice;
      const gasCostEth = formatUnits(gasCost);
      
      // Build confirmation details
      const details = {
        type: operationType === 'wrap' ? 
          `Wrap ${nativeToken.symbol} to W${nativeToken.symbol}` : 
          `Unwrap ${wrappedToken.symbol} to ${nativeToken.symbol}`,
        from: walletAddress,
        to: currentChain.wrappedNativeToken,
        spendingAmount: `${amount} ${operationType === 'wrap' ? nativeToken.symbol : wrappedToken.symbol}`,
        receivingAmount: `${amount} ${operationType === 'wrap' ? wrappedToken.symbol : nativeToken.symbol}`,
        gasFee: `${parseFloat(gasCostEth).toFixed(8)} ${nativeToken.symbol}`,
        operationType: operationType // Store the operation type explicitly
      };
      
      // Store confirmation details in state
      setConfirmationDetails(details);
      
      // Return both success and the details object
      return { success: true, details };
    } catch (err) {
      console.error("Error preparing wrap/unwrap:", err);
      setError("Failed to prepare transaction");
      return { success: false };
    }
  }, [currentWallet, provider, currentChain]);

  /**
   * Execute wrap operation (ETH -> WETH)
   * @param {string} amount - Amount to wrap
   * @param {object} gasSettings - Gas settings
   * @returns {object} - Transaction result
   */
  const executeWrap = useCallback(async (amount, gasSettings = {}) => {
    if (!provider || !currentWallet?.privateKey || !currentChain?.wrappedNativeToken) {
      setError("Missing requirements for this operation");
      return { success: false, error: "Missing requirements for this operation" };
    }
    
    // Reset transaction hash and set processing state
    setTxnHash(null);
    setIsProcessing(true);
    setIsPendingConfirmation(true);
    setError(null);
    
    try {
      // Create wallet with signer
      const wallet = new ethers.Wallet(currentWallet.privateKey, provider);
      
      // Get WETH contract
      const wethContract = new Contract(
        currentChain.wrappedNativeToken,
        WETH_ABI,
        wallet
      );
      
      // Parse amount
      const amountWei = parseUnits(amount, 18);
      
      // Prepare transaction options
      const txOptions = { 
        value: amountWei 
      };
      
      // Apply gas settings if provided
      if (gasSettings?.gasOption === 'custom' && gasSettings?.gasPrice) {
        try {
          txOptions.gasPrice = parseUnits(gasSettings.gasPrice, 'gwei');
        } catch (error) {
          console.error("Error setting custom gas price:", error);
        }
      }
      
      // Call the deposit function
      console.log(`Wrapping ${amount} ${currentChain.nativeToken.symbol} to ${currentChain.wrappedNativeToken}`);
      const tx = await wethContract.deposit(txOptions);
      
      // Add transaction to history
      addTransaction({
        hash: tx.hash,
        type: TRANSACTION_TYPES.WRAP,
        from: wallet.address,
        to: currentChain.wrappedNativeToken,
        value: amount,
        tokenSymbol: currentChain.nativeToken.symbol,
        tokenDecimal: 18,
        status: 'pending',
        network: contextChain?.key || 'unknown',
        data: JSON.stringify({
          wrappedToken: currentChain.wrappedNativeToken,
          action: 'wrap'
        }),
        explorerUrl: currentChain?.explorerUrl ? `${currentChain.explorerUrl}/tx/${tx.hash}` : ''
      });
      
      // Update UI with pending transaction hash
      setTxnHash(tx.hash);
      
      // Show notification if auto-confirm is enabled
      if (autoConfirm && notificationRef.current) {
        notificationRef.current.showProcessing(currentChain?.explorerUrl || '');
      }
      
      // Wait for confirmation
      const receipt = await tx.wait();
      
      // Transaction is confirmed, update UI
      setIsPendingConfirmation(false);
      
      // Update transaction status
      updateTransactionStatus(tx.hash, 'success');
      
      // Update notification if auto-confirm is enabled
      if (autoConfirm && notificationRef.current) {
        notificationRef.current.showSuccess(receipt.hash, currentChain?.explorerUrl || '');
      }
      
      setIsProcessing(false);
      
      return {
        success: true,
        hash: receipt.hash
      };
    } catch (err) {
      console.error("Error executing wrap:", err);
      
      // If we have a transaction hash but it failed, update status
      if (err.transaction && err.transaction.hash) {
        updateTransactionStatus(err.transaction.hash, 'failed');
      }
      
      // Set error message
      const errorMessage = err.message || "Failed to wrap";
      setError(errorMessage);
      
      // Reset UI states
      setIsPendingConfirmation(false);
      
      // Hide notification if auto-confirm is enabled
      if (autoConfirm && notificationRef.current) {
        notificationRef.current.hide();
      }
      
      setIsProcessing(false);
      
      return {
        success: false,
        error: errorMessage
      };
    }
  }, [provider, currentWallet, currentChain, addTransaction, updateTransactionStatus, contextChain, autoConfirm, notificationRef]);

  /**
   * Execute unwrap operation (WETH -> ETH)
   * @param {string} amount - Amount to unwrap
   * @param {object} gasSettings - Gas settings
   * @returns {object} - Transaction result
   */
  const executeUnwrap = useCallback(async (amount, gasSettings = {}) => {
    if (!provider || !currentWallet?.privateKey || !currentChain?.wrappedNativeToken) {
      setError("Missing requirements for this operation");
      return { success: false, error: "Missing requirements for this operation" };
    }
    
    // Reset transaction hash and set processing state
    setTxnHash(null);
    setIsProcessing(true);
    setIsPendingConfirmation(true);
    setError(null);
    
    try {
      // Create wallet with signer
      const wallet = new ethers.Wallet(currentWallet.privateKey, provider);
      
      // Get WETH contract
      const wethContract = new Contract(
        currentChain.wrappedNativeToken,
        WETH_ABI,
        wallet
      );
      
      // Parse amount
      const amountWei = parseUnits(amount, 18);
      
      // Prepare transaction options
      const txOptions = {};
      
      // Apply gas settings if provided
      if (gasSettings?.gasOption === 'custom' && gasSettings?.gasPrice) {
        try {
          txOptions.gasPrice = parseUnits(gasSettings.gasPrice, 'gwei');
        } catch (error) {
          console.error("Error setting custom gas price:", error);
        }
      }
      
      // Call the withdraw function
      console.log(`Unwrapping ${amount} WETH to native ${currentChain.nativeToken.symbol}`);
      const tx = await wethContract.withdraw(amountWei, txOptions);
      
      // Add transaction to history
      addTransaction({
        hash: tx.hash,
        type: TRANSACTION_TYPES.UNWRAP,
        from: wallet.address,
        to: currentChain.wrappedNativeToken,
        value: amount,
        tokenSymbol: 'WETH', // We could use dynamically fetched symbol here
        tokenDecimal: 18,
        status: 'pending',
        network: contextChain?.key || 'unknown',
        data: JSON.stringify({
          wrappedToken: currentChain.wrappedNativeToken,
          action: 'unwrap'
        }),
        explorerUrl: currentChain?.explorerUrl ? `${currentChain.explorerUrl}/tx/${tx.hash}` : ''
      });
      
      // Update UI with pending transaction hash
      setTxnHash(tx.hash);
      
      // Show notification if auto-confirm is enabled
      if (autoConfirm && notificationRef.current) {
        notificationRef.current.showProcessing(currentChain?.explorerUrl || '');
      }
      
      // Wait for confirmation
      const receipt = await tx.wait();
      
      // Transaction is confirmed, update UI
      setIsPendingConfirmation(false);
      
      // Update transaction status
      updateTransactionStatus(tx.hash, 'success');
      
      // Update notification if auto-confirm is enabled
      if (autoConfirm && notificationRef.current) {
        notificationRef.current.showSuccess(receipt.hash, currentChain?.explorerUrl || '');
      }
      
      setIsProcessing(false);
      
      return {
        success: true,
        hash: receipt.hash
      };
    } catch (err) {
      console.error("Error executing unwrap:", err);
      
      // If we have a transaction hash but it failed, update status
      if (err.transaction && err.transaction.hash) {
        updateTransactionStatus(err.transaction.hash, 'failed');
      }
      
      // Set error message
      const errorMessage = err.message || "Failed to unwrap";
      setError(errorMessage);
      
      // Reset UI states
      setIsPendingConfirmation(false);
      
      // Hide notification if auto-confirm is enabled
      if (autoConfirm && notificationRef.current) {
        notificationRef.current.hide();
      }
      
      setIsProcessing(false);
      
      return {
        success: false,
        error: errorMessage
      };
    }
  }, [provider, currentWallet, currentChain, addTransaction, updateTransactionStatus, contextChain, autoConfirm, notificationRef]);

/**
 * Handle confirmation of wrap/unwrap from modal - MODIFIED to accept details directly
 * @param {object} gasSettings - Gas settings from modal
 * @param {string} currentAmount - Current amount from component state
 * @param {object} detailsOverride - Direct details object to use instead of state
 * @returns {object} - Transaction result
 */
const handleConfirmation = useCallback(async (gasSettings = {}, currentAmount = null, detailsOverride = null) => {
    // Use provided details or fall back to state
    const details = detailsOverride || confirmationDetails;
    
    if (!details) {
      console.error("No confirmation details available in handleConfirmation");
      setError("Missing transaction details");
      return { success: false, error: "Missing transaction details" };
    }
    
    // Get the current operation type based on current tokens
    const currentOperation = getCurrentOperation();
    
    if (!currentOperation) {
      setError("Invalid operation - tokens have changed");
      return { success: false, error: "Invalid operation - tokens have changed" };
    }
    
    // Use the current operation type, not the one from confirmation details
    const operationType = currentOperation.type;
    
    // Use the provided current amount if available
    let amount;
    if (currentAmount !== null && currentAmount !== undefined && currentAmount !== '') {
      amount = currentAmount;
      console.log(`Using provided current amount: ${amount}`);
    } else {
      // Fall back to the amount from details
      const { spendingAmount } = details;
      amount = spendingAmount.split(' ')[0];
      console.log(`Using amount from details: ${amount}`);
    }
    
    console.log(`Executing ${operationType} operation with amount ${amount}`);
    
    if (operationType === 'wrap') {
      return executeWrap(amount, gasSettings);
    } else if (operationType === 'unwrap') {
      return executeUnwrap(amount, gasSettings);
    } else {
      setError("Invalid operation type");
      return { success: false, error: "Invalid operation type" };
    }
  }, [confirmationDetails, executeWrap, executeUnwrap, getCurrentOperation]);

  return {
    isWrapUnwrapOperation,
    getCurrentOperation,
    prepareConfirmation,
    executeWrap,
    executeUnwrap,
    handleConfirmation,
    isProcessing,
    isPendingConfirmation,
    txnHash,
    error,
    setError,
    confirmationDetails,
    isConfirmationModalOpen,
    setIsConfirmationModalOpen
  };
};

export default useWrapUnwrap;